import type { SingleConditionMetric } from 'services/conditions/useConditionMetrics';

const FLAG_COLOR_RANK: Record<string, number> = { red: 0, orange: 1 };
const NON_FLAG_COLOR_RANK: Record<string, number> = { grey: 0, green: 1 };

export const isFlaggedColor = (color: string) =>
  color === 'red' || color === 'orange';

export type ConditionSectionGroup = {
  section: string;
  metrics: SingleConditionMetric[];
};

export const getFlaggedMetrics = (metrics: SingleConditionMetric[]) =>
  metrics.filter(m => isFlaggedColor(m.evaluation_color));

export const getNonFlaggedMetrics = (metrics: SingleConditionMetric[]) =>
  metrics.filter(m => !isFlaggedColor(m.evaluation_color));

/**
 * Group metrics by key_insights_section — sections ordered by section priority;
 * within a section, by category order (section_order) then the color ranking.
 */
const groupBySection = (
  metrics: SingleConditionMetric[],
  colorRank: Record<string, number>,
): ConditionSectionGroup[] => {
  const groups = new Map<
    string,
    ConditionSectionGroup & { sectionPriority: number }
  >();

  metrics.forEach(metric => {
    const key = metric.section ?? '';
    const existing = groups.get(key);
    if (existing) {
      existing.metrics.push(metric);
    } else {
      groups.set(key, {
        section: key,
        sectionPriority: metric.section_priority ?? Number.NEGATIVE_INFINITY,
        metrics: [metric],
      });
    }
  });

  return [...groups.values()]
    .sort((a, b) => b.sectionPriority - a.sectionPriority)
    .map(group => ({
      section: group.section,
      metrics: [...group.metrics].sort(
        (a, b) =>
          (a.section_order ?? Number.MAX_SAFE_INTEGER) -
            (b.section_order ?? Number.MAX_SAFE_INTEGER) ||
          (colorRank[a.evaluation_color] ?? 99) -
            (colorRank[b.evaluation_color] ?? 99),
      ),
    }));
};

/**
 * Flagged metrics grouped by section (sections by priority); within a section,
 * category order then reds before yellows.
 */
export const groupFlaggedBySection = (
  metrics: SingleConditionMetric[],
): ConditionSectionGroup[] =>
  groupBySection(getFlaggedMetrics(metrics), FLAG_COLOR_RANK);

/**
 * Non-flagged metrics grouped by section (same order as flagged); within a
 * section, category order then grays before greens.
 */
export const groupNonFlaggedBySection = (
  metrics: SingleConditionMetric[],
): ConditionSectionGroup[] =>
  groupBySection(getNonFlaggedMetrics(metrics), NON_FLAG_COLOR_RANK);
